import { MOCK_DB_NAME, MOCK_DB_VERSION } from '../constants';
import { buildResult } from '../helpers';

const hasIndexedDb = typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';

const storeNameFor = (namespace, collection) => `${namespace}:${collection}`;
const idbError = (code, message, cause) => {
  const error = new Error(message);
  error.code = code;
  if (cause) {
    error.cause = cause;
  }
  return error;
};

export const createIndexedDbBackend = () => {
  let dbPromise = null;
  let dbVersion = MOCK_DB_VERSION;
  let ensureStoreLock = Promise.resolve();

  const openDb = (version, storeNameToCreate) =>
    new Promise((resolve, reject) => {
      if (!hasIndexedDb) {
        reject(idbError('IDB_UNAVAILABLE', 'IndexedDB unavailable'));
        return;
      }

      let request;
      try {
        request = window.indexedDB.open(MOCK_DB_NAME, version);
      } catch (error) {
        reject(idbError('IDB_OPEN_FAILED', 'IndexedDB open failed', error));
        return;
      }

      request.onupgradeneeded = () => {
        const db = request.result;

        try {
          if (storeNameToCreate && !db.objectStoreNames.contains(storeNameToCreate)) {
            db.createObjectStore(storeNameToCreate, { keyPath: 'id' });
          }
        } catch (error) {
          reject(idbError('IDB_UPGRADE_FAILED', 'IndexedDB upgrade failed', error));
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(idbError('IDB_OPEN_FAILED', 'IndexedDB open failed', request.error));
    });

  const getDb = async () => {
    if (!dbPromise) {
      dbPromise = openDb(dbVersion);
    }

    return dbPromise;
  };

  const ensureStoreUnsafe = async storeName => {
    let db;
    try {
      db = await getDb();
    } catch (error) {
      throw error && error.code ? error : idbError('IDB_OPEN_FAILED', 'IndexedDB open failed', error);
    }

    if (db.objectStoreNames.contains(storeName)) {
      return db;
    }

    db.close();
    dbVersion += 1;
    dbPromise = openDb(dbVersion, storeName);
    try {
      db = await dbPromise;
    } catch (error) {
      throw error && error.code ? error : idbError('IDB_UPGRADE_FAILED', 'IndexedDB upgrade failed', error);
    }

    return db;
  };

  const ensureStore = storeName => {
    const run = ensureStoreLock.then(() => ensureStoreUnsafe(storeName));
    ensureStoreLock = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  };

  const withStore = async (namespace, collection, mode, handler) => {
    const storeName = storeNameFor(namespace, collection);
    const db = await ensureStore(storeName);

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(storeName, mode);
      const store = transaction.objectStore(storeName);

      transaction.onerror = () =>
        reject(idbError('IDB_TX_FAILED', 'IndexedDB transaction failed', transaction.error));

      handler({
        store,
        resolve,
        reject
      });
    });
  };

  return {
    async list(namespace, collection) {
      try {
        const data = await withStore(
          namespace,
          collection,
          'readonly',
          ({ store, resolve, reject }) => {
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => reject(request.error || new Error('IndexedDB list failed'));
          }
        );

        return buildResult({ ok: true, data });
      } catch (error) {
        return buildResult({ ok: false, error });
      }
    },

    async getById(namespace, collection, id) {
      try {
        const data = await withStore(
          namespace,
          collection,
          'readonly',
          ({ store, resolve, reject }) => {
            const request = store.get(id);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error || new Error('IndexedDB getById failed'));
          }
        );

        return buildResult({ ok: true, data });
      } catch (error) {
        return buildResult({ ok: false, error });
      }
    },

    async create(namespace, collection, payload) {
      try {
        await withStore(namespace, collection, 'readwrite', ({ store, resolve, reject }) => {
          const request = store.add(payload);
          request.onsuccess = () => resolve(true);
          request.onerror = () => reject(request.error || new Error('IndexedDB create failed'));
        });

        return buildResult({ ok: true, data: payload });
      } catch (error) {
        return buildResult({ ok: false, error });
      }
    },

    async update(namespace, collection, id, payload) {
      try {
        const current = await withStore(
          namespace,
          collection,
          'readonly',
          ({ store, resolve, reject }) => {
            const request = store.get(id);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () =>
              reject(request.error || new Error('IndexedDB update read failed'));
          }
        );

        if (!current) {
          return buildResult({ ok: false, error: new Error('Item not found') });
        }

        await withStore(namespace, collection, 'readwrite', ({ store, resolve, reject }) => {
          const request = store.put(payload);
          request.onsuccess = () => resolve(true);
          request.onerror = () => reject(request.error || new Error('IndexedDB update failed'));
        });

        return buildResult({ ok: true, data: payload });
      } catch (error) {
        return buildResult({ ok: false, error });
      }
    },

    async remove(namespace, collection, id) {
      try {
        await withStore(namespace, collection, 'readwrite', ({ store, resolve, reject }) => {
          const request = store.delete(id);
          request.onsuccess = () => resolve(true);
          request.onerror = () => reject(request.error || new Error('IndexedDB remove failed'));
        });

        return buildResult({ ok: true, data: { removedId: id } });
      } catch (error) {
        return buildResult({ ok: false, error });
      }
    },

    async clear(namespace, collection) {
      try {
        if (collection) {
          await withStore(namespace, collection, 'readwrite', ({ store, resolve, reject }) => {
            const request = store.clear();
            request.onsuccess = () => resolve(true);
            request.onerror = () => reject(request.error || new Error('IndexedDB clear failed'));
          });
        } else {
          const db = await getDb();
          const names = [];

          for (let i = 0; i < db.objectStoreNames.length; i += 1) {
            const storeName = db.objectStoreNames.item(i);
            if (storeName && storeName.indexOf(`${namespace}:`) === 0) {
              names.push(storeName);
            }
          }

          await Promise.all(
            names.map(
              storeName =>
                new Promise((resolve, reject) => {
                  const transaction = db.transaction(storeName, 'readwrite');
                  const store = transaction.objectStore(storeName);
                  const request = store.clear();
                  request.onsuccess = () => resolve(true);
                  request.onerror = () =>
                    reject(request.error || new Error('IndexedDB clear namespace failed'));
                })
            )
          );
        }

        return buildResult({ ok: true, data: true });
      } catch (error) {
        return buildResult({ ok: false, error });
      }
    }
  };
};
